import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { SunIcon, MoonIcon, Bars3Icon } from '@heroicons/react/24/outline';
import { AlertCountIndicator, UserMenu, DemoResetBanner, useDemoReset, onAlertCountsChanged } from '@trinetra/shared';
import api, { getNodeActiveAlertSummary } from '../../services/api.service';
import { useNode } from '../../contexts/NodeContext';
import { USER_MENU_NAV } from '../../constants/navigation';
import SiteSwitcher from './SiteSwitcher';

/** How often both badges re-ask. Slow on purpose — see the note in the local app's header. */
const POLL_MS = 60000;

/** What /system-alerts/summary reports. Severities are the service-error vocabulary, which is
 *  NOT the AI one: it has 'error' between critical and warning. */
interface SystemSummary {
  /** Still nobody's — the badge's number. */
  unacknowledged_count?: number;
  unacknowledged_critical?: number;
  unacknowledged_error?: number;
  unacknowledged_warning?: number;
  /** Everything unresolved, including what is already being worked on. */
  active_count?: number;
}

/** What a node's /alerts/active-summary reports: `total` and the severities are alerts nobody
 *  has picked up; `open_total` / `acknowledged_open` are the ones already in hand. */
interface AiSummary {
  total?: number;
  critical?: number;
  warning?: number;
  normal?: number;
  open_total?: number;
  acknowledged_open?: number;
}

interface HeaderProps {
  onMobileMenuToggle: () => void;
}

export default function Header({ onMobileMenuToggle }: HeaderProps) {
  const { user, logout, hasPermission } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const { selectedNodeId } = useNode();
  // Null unless the SELECTED site is a demo. Owned by useDemoResetFeed (mounted in Layout),
  // which asks the selected node directly and clears on every scope change — including All
  // Sites, where a one-site countdown could only ever be a claim about all of them.
  const demoReset = useDemoReset();

  const canViewSystemAlerts = hasPermission('system_alert.view');
  const canViewAlerts = hasPermission('alerts.view');

  /** SYSTEM / SERVICE alerts — crashed services, unhealthy nodes. Estate-wide. */
  const [system, setSystem] = useState<SystemSummary | null>(null);
  /** AI ANALYTICS alerts — what the cameras saw, on the SELECTED site. */
  const [ai, setAi] = useState<AiSummary | null>(null);

  /**
   * The system meter reads /system-alerts/summary, which is the page the icon opens.
   *
   * It used to read /alert-system/summary — a DIFFERENT domain (system-resource rules, not
   * service errors) — so the number in the title bar was counting one thing and the page it
   * led to listed another. A badge whose count cannot be found on its own destination is
   * worse than no badge.
   */
  useEffect(() => {
    if (!canViewSystemAlerts) return;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await api.get('/system-alerts/summary');
        const data = res.data?.data || res.data;
        if (!cancelled) setSystem(data || {});
      } catch { /* a badge is not worth an error surface — it stays as it was */ }
    };
    load();
    const timer = setInterval(load, POLL_MS);
    /* The operator's own acknowledgements, straight away — waiting for the poll made the badge
       look broken in the one situation where somebody watches it (alertCountsBus). Plus a
       refresh when the tab comes back, so a header hidden for an hour is not showing an
       hour-old number. */
    const stopListening = onAlertCountsChanged(load);
    const onVisible = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearInterval(timer);
      stopListening();
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [canViewSystemAlerts]);

  /**
   * The AI meter is per-SITE, and deliberately silent on All Sites: the count lives on the
   * nodes, so an estate-wide figure would be one proxied request per site on every poll from
   * every open tab. Same call the DemoResetBanner makes about scope — a number that is only
   * true of one site must not be shown as if it were true of all of them. The tooltip says so
   * rather than leaving an empty meter to be read as "nothing is wrong".
   */
  useEffect(() => {
    setAi(null);
    if (!canViewAlerts || !selectedNodeId) return;
    let cancelled = false;
    const load = () => {
      getNodeActiveAlertSummary(selectedNodeId)
        .then((data) => { if (!cancelled) setAi(data || {}); })
        .catch(() => { /* an offline site answers nothing; the meter simply stays blank */ });
    };
    load();
    const timer = setInterval(load, POLL_MS);
    const stopListening = onAlertCountsChanged(load);
    const onVisible = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearInterval(timer);
      stopListening();
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [canViewAlerts, selectedNodeId]);

  return (
    // z-40, not z-30: <main> sets no stacking context, so page content escapes into the root
    // one and competes with this header directly. The alert panel's live tiles and the board's
    // click-preview reach z-30 and, coming later in the DOM, were painting over the user-menu
    // dropdown. Modals and the layout dropdown stay at z-50, so they still cover the header —
    // which is what they are for.
    <header className="flex h-14 items-center justify-between border-b border-border bg-card px-6 sticky top-0 z-40">
      {/* Left — mobile hamburger, then the scope control. The switcher lives here rather than
          in the sidebar because it governs everything in the sidebar: on screen permanently,
          above the menu it scopes, and it can never scroll out of view. */}
      <div className="flex min-w-0 items-center gap-3">
        <button
          onClick={onMobileMenuToggle}
          className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors lg:hidden"
          title="Toggle menu"
        >
          <Bars3Icon className="h-5 w-5" />
        </button>
        <SiteSwitcher />
        {/* On a demo, the one thing every page has to say: these alerts, events and attendance
            rows are wiped on a timer, and here is how long is left. In the title bar because
            it is true of the whole site, not of any one page. */}
        <DemoResetBanner info={demoReset} className="hidden lg:flex" />
      </div>

      {/* Right */}
      <div className="flex items-center gap-3">
        {/* Narrow screens: the countdown alone — the sentence is in its tooltip. */}
        <DemoResetBanner info={demoReset} compact className="lg:hidden" />
        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {theme === 'dark' ? <SunIcon className="h-5 w-5" /> : <MoonIcon className="h-5 w-5" />}
        </button>

        {/* The two alert domains, as two meters. One bell could only ever be ambiguous: "7"
            might be seven people in a restricted area or seven service restarts, and those go
            to different people and different pages. See AlertCountIndicator. */}
        {canViewAlerts && (
          <AlertCountIndicator
            domain="ai"
            total={ai ? ai.total ?? 0 : null}
            breakdown={ai ? [
              { label: 'Critical', count: ai.critical || 0, tone: 'critical' },
              { label: 'Warning', count: ai.warning || 0, tone: 'warning' },
              { label: 'Info', count: ai.normal || 0, tone: 'info' },
            ] : []}
            secondary={ai ? { label: 'Acknowledged, still open', count: ai.acknowledged_open || 0 } : undefined}
            note={selectedNodeId ? undefined : 'Pick a single site to see its AI alert counts'}
            onOpen={() => navigate('/alerts')}
          />
        )}
        {canViewSystemAlerts && (
          <AlertCountIndicator
            domain="system"
            total={system ? system.unacknowledged_count ?? 0 : null}
            breakdown={system ? [
              { label: 'Critical', count: system.unacknowledged_critical || 0, tone: 'critical' },
              { label: 'Error', count: system.unacknowledged_error || 0, tone: 'critical' },
              { label: 'Warning', count: system.unacknowledged_warning || 0, tone: 'warning' },
            ] : []}
            secondary={system ? {
              label: 'Acknowledged, still active',
              count: Math.max(0, (system.active_count || 0) - (system.unacknowledged_count || 0)),
            } : undefined}
            onOpen={() => navigate('/system-alerts')}
          />
        )}

        {/* User menu — platform admin surfaces + logout */}
        <UserMenu
          items={USER_MENU_NAV}
          hasPermission={hasPermission}
          user={{ name: user?.name, role: user?.role }}
          onLogout={logout}
        />
      </div>
    </header>
  );
}
